import { useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, CheckCircle2 } from "lucide-react";
import PageHero from "@/components/PageHero";
import { cn } from "@/lib/utils";
import { useI18n } from "@/i18n/I18nProvider";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AuditPage() {
  const { t, lang } = useI18n();
  const steps = t("audit.steps") || [];
  const totalSteps = steps.length + 1; // questions + contact step

  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({}); // qIndex -> optionId
  const [contact, setContact] = useState({ name: "", email: "", company: "", context: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const pickOption = (qIndex, optionId) => {
    const next = { ...answers, [qIndex]: optionId };
    setAnswers(next);
    if (qIndex < steps.length - 1) {
      setStep(qIndex + 1);
    } else {
      setStep(steps.length); // move to contact
    }
  };

  const validate = () => {
    if (!contact.name.trim()) return t("audit.errors.name");
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contact.email)) return t("audit.errors.email");
    return null;
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    const err = validate();
    if (err) {
      toast.error(err);
      return;
    }
    setSubmitting(true);
    try {
      // Build a readable answers map
      const answersByQ = steps.reduce((acc, s, qIndex) => {
        const optId = answers[qIndex];
        if (!optId) return acc;
        const opt = (s.options || []).find((o) => o.id === optId);
        const label = opt?.label || optId;
        acc[s.q] = label;
        return acc;
      }, {});

      await axios.post(`${API}/audit`, {
        answers: answersByQ,
        name: contact.name.trim(),
        email: contact.email,
        company: contact.company.trim() || null,
        context: contact.context.trim() || null,
        lang,
      });
      setDone(true);
    } catch (e2) {
      console.error(e2);
      toast.error(e2?.response?.data?.detail || t("audit.errors.generic"));
    } finally {
      setSubmitting(false);
    }
  };

  const progress = useMemo(() => {
    if (done) return 100;
    return Math.round(((step) / totalSteps) * 100);
  }, [step, totalSteps, done]);

  if (done) {
    return (
      <>
        <PageHero
          eyebrow={t("audit.hero.eyebrow")}
          title={t("audit.success.title")}
          description={t("audit.success.description")}
        />
        <section className="bg-brand-off py-20 md:py-28">
          <div className="container-x max-w-2xl text-center">
            <CheckCircle2 className="w-12 h-12 text-brand-blue mx-auto" strokeWidth={1.4} />
            <div className="mt-10">
              <Link
                to="/"
                data-testid="audit-back-home"
                className="inline-flex items-center gap-2 rounded-full bg-brand-charcoal text-brand-off px-6 py-3.5 text-[14px] font-medium hover:bg-brand-dark transition-colors"
              >
                {t("audit.success.action")} <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          </div>
        </section>
      </>
    );
  }

  const onContactStep = step >= steps.length;

  return (
    <>
      <PageHero
        eyebrow={t("audit.hero.eyebrow")}
        title={t("audit.hero.title")}
        description={t("audit.hero.description")}
      />

      <section data-testid="audit-section" className="bg-brand-off py-20 md:py-28">
        <div className="container-x max-w-3xl">
          {/* Progress */}
          <div className="flex items-center justify-between mb-6">
            <span className="eyebrow" data-testid="audit-step-indicator">
              {onContactStep
                ? `${t("audit.progress.step")} ${totalSteps} ${t("audit.progress.of")} ${totalSteps}`
                : `${t("audit.progress.step")} ${step + 1} ${t("audit.progress.of")} ${totalSteps}`}
            </span>
            <span className="text-[12.5px] text-brand-muted">{progress}%</span>
          </div>
          <div className="h-1 w-full bg-brand-line rounded-full overflow-hidden mb-12">
            <div
              data-testid="audit-progress"
              className="h-full bg-brand-charcoal transition-all duration-500"
              style={{ width: `${progress}%` }}
            />
          </div>

          {!onContactStep && (
            <div data-testid={`audit-q-${step + 1}`}>
              <h2 className="text-3xl md:text-[36px] font-light tracking-tight leading-[1.1] text-brand-charcoal max-w-2xl">
                {steps[step].q}
              </h2>

              <div className="mt-10 grid grid-cols-1 gap-3">
                {(steps[step].options || []).map((o, i) => {
                  const picked = answers[step] === o.id;
                  return (
                    <button
                      key={o.id}
                      type="button"
                      data-testid={`audit-option-${step + 1}-${i + 1}`}
                      onClick={() => pickOption(step, o.id)}
                      className={cn(
                        "group text-left rounded-2xl px-6 py-5 border bg-white transition-all hover-lift soft-shadow soft-shadow-hover",
                        picked
                          ? "border-brand-charcoal/60"
                          : "border-black/[0.06]",
                      )}
                    >
                      <div className="flex items-center justify-between gap-4">
                        <span className="text-[16px] text-brand-charcoal leading-snug">{o.label}</span>
                        <ArrowRight className="w-4 h-4 text-brand-muted transition-transform group-hover:translate-x-1 shrink-0" />
                      </div>
                    </button>
                  );
                })}
              </div>

              <div className="mt-10 flex items-center justify-between">
                <button
                  type="button"
                  disabled={step === 0}
                  onClick={() => setStep((s) => Math.max(0, s - 1))}
                  data-testid="audit-back"
                  className="inline-flex items-center gap-2 text-[14px] text-brand-muted hover:text-brand-charcoal disabled:opacity-40 transition-colors"
                >
                  <ArrowLeft className="w-3.5 h-3.5" /> {t("common.back")}
                </button>
              </div>
            </div>
          )}

          {onContactStep && (
            <form
              onSubmit={submit}
              data-testid="audit-contact-form"
              className="rounded-2xl bg-white border border-black/[0.06] p-8 md:p-10 soft-shadow grid grid-cols-1 gap-6"
            >
              <div>
                <p className="eyebrow mb-4">{t("audit.progress.step")} {totalSteps}</p>
                <h2 className="text-3xl md:text-[36px] font-light tracking-tight leading-[1.1] text-brand-charcoal">
                  {t("audit.contact.title")}
                </h2>
                <p className="mt-3 text-brand-muted text-[15px] leading-relaxed">
                  {t("audit.contact.description")}
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Field label={t("audit.contact.fields.name")} required>
                  <input
                    type="text"
                    value={contact.name}
                    onChange={(e) => setContact({ ...contact, name: e.target.value })}
                    data-testid="audit-input-name"
                    className={inputCls}
                    maxLength={200}
                  />
                </Field>
                <Field label={t("audit.contact.fields.email")} required>
                  <input
                    type="email"
                    value={contact.email}
                    onChange={(e) => setContact({ ...contact, email: e.target.value })}
                    data-testid="audit-input-email"
                    className={inputCls}
                  />
                </Field>
              </div>

              <Field label={t("audit.contact.fields.company")}>
                <input
                  type="text"
                  value={contact.company}
                  onChange={(e) => setContact({ ...contact, company: e.target.value })}
                  data-testid="audit-input-company"
                  className={inputCls}
                  maxLength={200}
                />
              </Field>

              <Field label={t("audit.contact.fields.context")}>
                <textarea
                  value={contact.context}
                  onChange={(e) => setContact({ ...contact, context: e.target.value })}
                  rows={4}
                  data-testid="audit-input-context"
                  className={`${inputCls} min-h-[110px] resize-y leading-relaxed`}
                  maxLength={2000}
                />
              </Field>

              <div className="flex items-center justify-between pt-2">
                <button
                  type="button"
                  onClick={() => setStep(steps.length - 1)}
                  data-testid="audit-back-from-contact"
                  className="inline-flex items-center gap-2 text-[14px] text-brand-muted hover:text-brand-charcoal transition-colors"
                >
                  <ArrowLeft className="w-3.5 h-3.5" /> {t("common.back")}
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="audit-submit"
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-brand-charcoal text-brand-off px-6 py-3.5 text-[14px] font-medium hover:bg-brand-dark transition-colors disabled:opacity-50"
                >
                  {submitting ? t("common.sending") : t("audit.contact.submit")}
                  {!submitting && <ArrowRight className="w-3.5 h-3.5" />}
                </button>
              </div>
            </form>
          )}
        </div>
      </section>
    </>
  );
}

const inputCls =
  "w-full h-12 rounded-xl border border-brand-line bg-brand-paper px-4 text-[15px] text-brand-charcoal placeholder:text-brand-muted focus:outline-none focus:border-brand-charcoal/40 focus:ring-2 focus:ring-brand-blue/20 transition-colors";

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="block mb-2 text-[12.5px] uppercase tracking-[0.16em] text-brand-muted font-medium">
        {label} {required && <span className="text-brand-blue">*</span>}
      </span>
      {children}
    </label>
  );
}
